import { type FC, useState, useRef, useEffect } from 'react';
import type { AdvancedFilters } from '@/lib/statisticsAnalysis';

export type { AdvancedFilters };

interface AdvancedFilterBarProps {
  filters: AdvancedFilters;
  onChange: (filters: AdvancedFilters) => void;
  totalFilteredDraws: number;
  totalAllDraws: number;
}

const NUMBER_RANGES = [
  { label: '1~10번대', value: 1, short: '1~10' },
  { label: '11~20번대', value: 2, short: '11~20' },
  { label: '21~30번대', value: 3, short: '21~30' },
  { label: '31~40번대', value: 4, short: '31~40' },
  { label: '41~45번대', value: 5, short: '41~45' },
];

const DAY_LABELS = [
  { label: '일', value: 0 },
  { label: '월', value: 1 },
  { label: '화', value: 2 },
  { label: '수', value: 3 },
  { label: '목', value: 4 },
  { label: '금', value: 5 },
  { label: '토', value: 6 },
];

const PRIZE_RANGES = [
  { label: '전체', value: '' },
  { label: '1억 미만', value: 'under1eok' },
  { label: '1억~5억', value: '1to5eok' },
  { label: '5억~10억', value: '5to10eok' },
  { label: '10억~30억', value: '10to30eok' },
  { label: '30억 이상', value: 'over30eok' },
];

const AdvancedFilterBar: FC<AdvancedFilterBarProps> = ({ filters, onChange, totalFilteredDraws, totalAllDraws }) => {
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded && panelRef.current) {
      panelRef.current.style.maxHeight = panelRef.current.scrollHeight + 'px';
    } else if (panelRef.current) {
      panelRef.current.style.maxHeight = '0px';
    }
  }, [expanded, filters]);

  const toggleNumberRange = (val: number) => {
    const next = filters.numberRanges.includes(val)
      ? filters.numberRanges.filter((v) => v !== val)
      : [...filters.numberRanges, val];
    onChange({ ...filters, numberRanges: next });
  };

  const toggleDayOfWeek = (val: number) => {
    const next = filters.dayOfWeeks.includes(val)
      ? filters.dayOfWeeks.filter((v) => v !== val)
      : [...filters.dayOfWeeks, val];
    onChange({ ...filters, dayOfWeeks: next });
  };

  const removeNumberRange = (val: number) => {
    onChange({ ...filters, numberRanges: filters.numberRanges.filter((v) => v !== val) });
  };

  const removeDayOfWeek = (val: number) => {
    onChange({ ...filters, dayOfWeeks: filters.dayOfWeeks.filter((v) => v !== val) });
  };

  const removePrizeRange = () => {
    onChange({ ...filters, prizeRange: '' });
  };

  const hasActiveFilters = filters.numberRanges.length > 0 || filters.dayOfWeeks.length > 0 || filters.prizeRange !== '';

  const activePrizeLabel = PRIZE_RANGES.find((pr) => pr.value === filters.prizeRange)?.label;

  return (
    <div className="space-y-3">
      {/* Toggle button + active filter chips */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setExpanded(!expanded)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
            hasActiveFilters
              ? 'bg-accent-50 border-accent-300 text-accent-700 shadow-[0_0_0_3px_rgba(251,191,36,0.15)]'
              : 'bg-background-100 border-background-300 text-foreground-500 hover:border-background-400'
          }`}
        >
          <i className={`text-sm ${expanded ? 'ri-arrow-up-s-line' : 'ri-filter-3-line'}`}></i>
          고급 필터
          {hasActiveFilters && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-accent-500 text-accent-950 text-[10px] font-bold">
              {filters.numberRanges.length + filters.dayOfWeeks.length + (filters.prizeRange ? 1 : 0)}
            </span>
          )}
        </button>

        {/* Search / apply button */}
        {!expanded && (
          <button
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line text-sm"></i>
            검색
          </button>
        )}
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line text-sm"></i>
            적용
          </button>
        )}

        {/* ---- Active filter chips with highlight ---- */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Number range chips */}
            {filters.numberRanges.map((val) => {
              const range = NUMBER_RANGES.find((r) => r.value === val);
              if (!range) return null;
              return (
                <span
                  key={`nr-${val}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700 border border-primary-300/70 shadow-[0_0_8px_rgba(20,33,61,0.15)] animate-[highlightPulsePrimary_2s_ease-in-out_infinite] cursor-default whitespace-nowrap"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                  {range.short}
                  <button
                    onClick={() => removeNumberRange(val)}
                    className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
                    aria-label={`${range.label} 필터 제거`}
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </span>
              );
            })}

            {/* Day of week chips */}
            {filters.dayOfWeeks.map((val) => {
              const day = DAY_LABELS.find((d) => d.value === val);
              if (!day) return null;
              const isWeekend = val === 0 || val === 6;
              return (
                <span
                  key={`dow-${val}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border cursor-default whitespace-nowrap ${
                    isWeekend
                      ? 'bg-accent-100 text-accent-700 border-accent-300/70 shadow-[0_0_8px_rgba(251,191,36,0.2)] animate-[highlightPulseAccent_2s_ease-in-out_infinite]'
                      : 'bg-secondary-100 text-secondary-700 border-secondary-300/70 shadow-[0_0_8px_rgba(20,184,166,0.2)] animate-[highlightPulseSecondary_2s_ease-in-out_infinite]'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isWeekend ? 'bg-accent-400' : 'bg-secondary-400'}`}></span>
                  {day.label}요일
                  <button
                    onClick={() => removeDayOfWeek(val)}
                    className={`ml-0.5 w-4 h-4 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                      isWeekend ? 'hover:bg-accent-200' : 'hover:bg-secondary-200'
                    }`}
                    aria-label={`${day.label}요일 필터 제거`}
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </span>
              );
            })}

            {/* Prize range chip */}
            {filters.prizeRange !== '' && activePrizeLabel && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 border border-accent-300/70 shadow-[0_0_8px_rgba(251,191,36,0.2)] animate-[highlightPulseAccent_2s_ease-in-out_infinite] cursor-default whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                💰 {activePrizeLabel}
                <button
                  onClick={removePrizeRange}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-accent-200 transition-colors cursor-pointer"
                  aria-label="당첨금 필터 제거"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </span>
            )}

            {/* Summary text */}
            <span className="text-xs text-foreground-400 ml-1">
              <strong className="text-foreground-800">{totalFilteredDraws.toLocaleString()}</strong> / {totalAllDraws.toLocaleString()}회차
            </span>

            {/* Reset all */}
            <button
              onClick={() => onChange({ numberRanges: [], dayOfWeeks: [], prizeRange: '' })}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line text-sm"></i>
              전체 초기화
            </button>
          </div>
        )}
      </div>

      {/* Expandable filter panel */}
      <div
        ref={panelRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: '0px' }}
      >
        <div className="bg-background-50 rounded-2xl border border-background-200 p-5 space-y-5">
          {/* Number Range */}
          <div>
            <label className="block text-xs text-foreground-500 mb-2 font-medium">
              <i className="ri-hashtag mr-1"></i>번호대 (중복 선택 가능)
            </label>
            <div className="flex flex-wrap gap-2">
              {NUMBER_RANGES.map((r) => {
                const active = filters.numberRanges.includes(r.value);
                return (
                  <button
                    key={r.value}
                    onClick={() => toggleNumberRange(r.value)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                      active
                        ? 'bg-primary-100 border-primary-300 text-primary-700 ring-2 ring-primary-300/40 scale-105'
                        : 'bg-background-50 border-background-300 text-foreground-400 hover:border-background-400 hover:text-foreground-600'
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-foreground-300 mt-1.5">
              선택한 번호대에 속한 번호가 하나라도 포함된 회차만 표시됩니다.
            </p>
          </div>

          {/* Day of Week */}
          <div>
            <label className="block text-xs text-foreground-500 mb-2 font-medium">
              <i className="ri-calendar-line mr-1"></i>추첨 요일 (중복 선택 가능)
            </label>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((d) => {
                const active = filters.dayOfWeeks.includes(d.value);
                return (
                  <button
                    key={d.value}
                    onClick={() => toggleDayOfWeek(d.value)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                      active
                        ? d.value === 0 || d.value === 6
                          ? 'bg-accent-100 border-accent-300 text-accent-700 ring-2 ring-accent-300/40 scale-105'
                          : 'bg-secondary-100 border-secondary-300 text-secondary-700 ring-2 ring-secondary-300/40 scale-105'
                        : 'bg-background-50 border-background-300 text-foreground-400 hover:border-background-400 hover:text-foreground-600'
                    }`}
                  >
                    {d.label}요일
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-foreground-300 mt-1.5">
              로또 추첨은 토요일에 진행되지만, 과거 데이터에는 예외 날짜가 포함될 수 있습니다.
            </p>
          </div>

          {/* Prize Range */}
          <div>
            <label className="block text-xs text-foreground-500 mb-2 font-medium">
              <i className="ri-funds-line mr-1"></i>1등 당첨금 구간
            </label>
            <div className="flex flex-wrap gap-2">
              {PRIZE_RANGES.map((pr) => {
                const active = filters.prizeRange === pr.value;
                return (
                  <button
                    key={pr.value}
                    onClick={() => onChange({ ...filters, prizeRange: pr.value })}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                      active
                        ? 'bg-primary-100 border-primary-300 text-primary-700 ring-2 ring-primary-300/40 scale-105'
                        : 'bg-background-50 border-background-300 text-foreground-400 hover:border-background-400 hover:text-foreground-600'
                    }`}
                  >
                    {pr.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilterBar;